import {Clone} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Column, Row, RowLR, Select, Spinner, Text} from "react-vcomponents";
import {TextPlus} from "web-vcore";
import {ObjPath} from "../../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {GetProcSceneryKinds} from "../../../../../../Server/DB/mapObjects/SceneryAccessors.js";
import {ResolveSceneryKind} from "../../../../../../Server/DB/mapObjects/SceneryAccessors.js";
import {GetMapObjects} from "../../../../../../Server/DB/mapObjects.js";
import {ResolveMapObjectsForTerrain} from "../../../../../../Terrain/Heights/Poisson/@Shared/ConstraintRasterizer/MapObject_ForTerrain_Resolver.js";
import {ShowSceneryExportDialog} from "../../@Shared/AreaExportDialog.js";
import {MaterialMask} from "../../../../../../Terrain/Materials/@MaterialFeature.js";
import {PathSceneryFeature} from "../../../../../../Terrain/Scenery/@SceneryFeature.js";
import {SceneryRegionPlacementCount} from "../../../../../../Terrain/Scenery/Placement/SceneryPlacementCount.js";
import {GetProcSceneryKindsByID} from "../../../../../../Server/DB/mapObjects/SceneryAccessors.js";
import {PathEditorUI_SharedProps} from "../PathEditorUI.js";

const splitAt = 120;

/** PathEditorTab_Scenery — Set13 P6: the path's scenery-placement feature (`ObjPath.sceneryFeature`),
 *  mirroring the Set12 material tab (`PathEditorTab_Material`) term-for-term: an enable checkbox
 *  with preserve-on-disable, a kind-list editor with per-entry weights (Set16 §7 — a mixed forest),
 *  mode/strokeWidth/feather, the
 *  density overrides (spacing/presence), and a mask list.  Scenery is ORTHOGONAL to material
 *  (§4.10): this tab places discrete objects; it never implies a ground-material change.
 *
 *  Where a region is a PREDICATE (`SceneryRegion.densityAt(p)`), never a list of instances (§3.4),
 *  this tab authors the FEATURE, not instances. */
export const PathEditorTab_Scenery = observer_mgl((props: {} & PathEditorUI_SharedProps)=>{
	const {obj, path, map, Change} = props;
	const sf = path.sceneryFeature;

	// The map's scenery-kind catalogue — the same ids the renderer loads (`GetProcSceneryKinds`),
	// so an id chosen here always resolves at render time.
	const kinds = GetProcSceneryKinds(map.id);

	const allMapObjects = GetMapObjects(map.id);

	// The bridge's per-path world geometry (the renderer's own resolution path) — so the "Placed"
	// count below uses the SAME geometry + region collection as the live renderer.
	const terrainObjects = ResolveMapObjectsForTerrain(
		allMapObjects,
		/*resolvePathOriginPathIDs:*/ true,
		/*keepPathMeshLocalPoints:*/ false,
		/*calcPathMeshWorldPoints:*/ true,
	);
	const sceneryKindCatalogue = GetProcSceneryKindsByID(map.id);
	const thisRegionID = path.path_id || `${obj.id}_path${(obj.paths ?? []).indexOf(path)}`;
	// The placed count for THIS feature at full density (mpp 0) — the intrinsic density, so the
	// spacing spinner's effect on count is shown precisely and matches the fixture-based tests.
	// Computed reactively (map objects / kinds are accessors), so it updates on any edit.
	const placedCount = PathSceneryFeature.IsActive(sf, new Set(sceneryKindCatalogue.keys()))
		? SceneryRegionPlacementCount(terrainObjects, new Set(sceneryKindCatalogue.keys()), id=>ResolveSceneryKind(id, map.id), thisRegionID)
		: null;

	// Commit a mutated feature back to the path.  `Change` re-runs the parent's full-object
	// clone+diff, so we mutate a clone here and hand it over.
	const ChangeFeature = (mutator: (f: PathSceneryFeature)=>any)=>{
		Change(a=>{
			const next = Clone(a.sceneryFeature ?? new PathSceneryFeature());
			mutator(next);
			a.sceneryFeature = next;
		});
	};

	return (
		<Column mt={5}>
			<Row>
				<CheckBox text="Scenery feature (places discrete objects)" value={sf != null && !sf.disabled} onChange={val=>{
					Change(a=>{
						if (val) {
							// First enable: create the struct if it doesn't exist yet (preserving the
							// "if they're trying to enable and the struct doesn't exist yet, THEN add" rule).
							// Subsequent toggles clear `disabled` (set to undefined) rather than adding/removing
							// the struct, so the rest of the config is preserved across disable→re-enable.
							a.sceneryFeature ??= new PathSceneryFeature();
							a.sceneryFeature.disabled = undefined;
							// Auto-generate a stable path_id on first enable (drives region ids + RNG seeding).
							if (!a.path_id) a.path_id = ObjPath.NewPathID();
						} else {
							// Disable in-place (keep the struct + its config; consumers IsActive() now returns false).
							if (a.sceneryFeature) a.sceneryFeature.disabled = true;
						}
					});
				}}/>
				{sf != null &&
					<Button ml={5} mdIcon="delete" title="Delete scenery-feature data" onClick={()=>{
						Change(a=>{ a.sceneryFeature = undefined; });
					}}/>}
			</Row>
			{sf && <>
				{/* Set16 §7 — Kinds: the mixed-kind list. One object per lattice cell; the placed kind
				    for a cell is drawn by weight across this list (weight controls the MIX, not the
				    total density — the region's shared lattice + occupancy presence stay region-wide). */}
				<Row mt={5}>
					<TextPlus info={`
						The kinds this region distributes, each with a RELATIVE WEIGHT (Set16). One object is
						placed per lattice cell; the placed kind for a cell is drawn deterministically by weight
						across this list. The weight controls the MIX — the ratio of placed counts across kinds —
						never the total density (that is the shared lattice + presence). Weights are relative:
						the sum need not be 1, and 3:1 means three pines per oak. The region's overrides stay
						REGION-WIDE (finest sibling spacing = the shared lattice; no presence override ⇒ occupancy
						uses the MAX sibling presence). Per-kind overrides are out of scope — use weight to tune a
						kind's share.
					`.AsMultiline(0)}>Kinds:</TextPlus>
				</Row>
				{(sf.kinds ?? []).map((entry, i)=>(
					<Row key={i} mt={5}>
						<Select style={{flex: 1}} options={kinds.map(k=>({name: k.name || k.id, value: k.id}))}
							value={entry.kindID}
							onChange={val=>ChangeFeature(f=>{
								const next = [...(f.kinds ?? [])];
								next[i] = {...next[i], kindID: val as string};
								f.kinds = next;
							})}/>
						<Text ml={5} style={{alignSelf: "center"}}>Weight:</Text>
						<Spinner ml={5} step="any" min={0} value={entry.weight ?? 1}
							onChange={val=>ChangeFeature(f=>{
								const next = [...(f.kinds ?? [])];
								next[i] = {...next[i], weight: val ?? 0};
								f.kinds = next;
							})}/>
						<Button ml={5} mdIcon="arrow-up" title="Move up" enabled={i > 0} onClick={()=>{
							ChangeFeature(f=>f.kinds.Move(f.kinds[i], i - 1));
						}}/>
						<Button ml={5} mdIcon="arrow-down" title="Move down" enabled={i < sf.kinds.length - 1} onClick={()=>{
							ChangeFeature(f=>f.kinds.Move(f.kinds[i], i + 1));
						}}/>
						<Button ml={5} mdIcon="delete" title="Remove this kind from the mix" onClick={()=>ChangeFeature(f=>{
							f.kinds = (f.kinds ?? []).filter((_, j)=>j != i);
						})}/>
					</Row>
				))}
				<Row mt={5}>
					<Button ml={0} p="1px 8px" text="+ kind" title="Add a kind to the mix (its weight controls its share)"
						onClick={()=>ChangeFeature(f=>{
							const next = [...(f.kinds ?? [])];
							next.push({kindID: kinds[0]?.id ?? "", weight: 1});
							f.kinds = next;
						})}/>
				</Row>

				{/* Seed — an EXCLUSIVE seed perturbation for this region's placement streams only. The
					"reroll this feature" knob: it shifts which cells spawn + their jitter/variant/rotation
					WITHOUT changing density or any other region — for example, two side-by-side copies of
					a forest that would otherwise place identically can be decorrelated. */}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						An EXCLUSIVE seed perturbation for THIS region's placement streams (Set17 §2:
						presence / jitter / the mixed-kind draw).  Absent (0) = the region's RNG seeds purely
						from its stable path-id.  A NON-ZERO value re-seeds ONLY this region — it shifts
						which cells spawn and their jitter/variant/rotation — WITHOUT changing its density
						(occupancy/spacing/presence/feather), kinds, dominance, suppression, or any OTHER
						region.  Use it to decorrelate two copies of a forest that would otherwise place
						IDENTICALLY (two regions sharing a path-id, or mirrored geometry).  The per-instance
						rank (zoom-out thinning) is deliberately unaffected.
					`.AsMultiline(0)}>Seed:</TextPlus>
					<Spinner step="any" value={sf.seedOverride}
						onChange={val=>ChangeFeature(f=>f.seedOverride = val != null && val != 0 ? val : undefined)}/>
					<Button ml={5} mdIcon="dice-multiple" title="Pick a random seed. (re-rolls this region's scenery placements)" onClick={()=>ChangeFeature(f=>f.seedOverride = Math.floor(Math.random() * 0xFFFFFFFF) + 1)}/>
					<Button ml={5} mdIcon="delete" title="Clear (use the path-id seed)" onClick={()=>ChangeFeature(f=>f.seedOverride = undefined)}/>
				</RowLR>

				{/* Kind seed — WHEN SET, an EXCLUSIVE re-roll of ONLY the mixed-kind draw (which kind
					owns each cell), leaving the DISTRIBUTION/placement (held by Seed) untouched — handy
					when the layout is good but a kind is wrong (a huge tree where a bush was wanted). */}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						An OPTIONAL seed that perturbs ONLY the mixed-kind draw (which of this region's kinds
						owns each lattice cell, Set16).  Absent = the kind draw follows the Seed field above.
						WHEN SET it REPLACES Seed for the kind draw ALONE, so you can re-roll WHICH KIND
						appears at each spot WITHOUT moving the placements or changing density/spacing — e.g.
						a huge tree right in front of the Viewpoint camera can be re-rolled to a bush while
						everything else keeps its exact position.  Only meaningful for a mixed-kind region
						(more than one kind in the list above); single-kind regions ignore it.
					`.AsMultiline(0)}>Kind seed:</TextPlus>
					<Spinner value={sf.kindSeedOverride} onChange2={val=>ChangeFeature(f=>f.kindSeedOverride = val)}/>
					<Button ml={5} mdIcon="dice-multiple" title="Pick a random kind seed. (re-rolls WHICH kind each spot gets — placements unchanged)" onClick={()=>ChangeFeature(f=>f.kindSeedOverride = Math.floor(Math.random() * 0xFFFFFFFF) + 1)}/>
					<Button ml={5} mdIcon="delete" title="Clear (the kind draw follows the Seed field)" onClick={()=>ChangeFeature(f=>f.kindSeedOverride = undefined)}/>
				</RowLR>

				{/* Mode — how the path's geometry becomes a scenery region. */}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						How the path's geometry becomes a scenery region (Set13 §3.3 — all three modes are
						ONE signed-distance formula, so this is free at runtime):
						* Fill         -> the interior of a CLOSED path is populated; nothing outside.
						* Stroke       -> terrain within "Stroke width"/2 metres of the polyline is populated
										 (works on open paths too — riversides, ridgelines).
						* Fill + stroke -> the interior PLUS a stroke-width apron spilling outside the ring
										 (an inner fill plus an outer skirt — one region, one SDF).
						Fill / Fill+stroke require the path to be closed (the curve's Closed checkbox above).
					`.AsMultiline(0)}>Mode:</TextPlus>
					<Select displayType="button bar" options={[
						{name: "Fill", value: "fill"},
						{name: "Stroke", value: "stroke"},
						{name: "Fill + stroke", value: "fillAndStroke"},
					]} value={sf.mode} onChange={val=>ChangeFeature(f=>f.mode = val as "fill" | "stroke" | "fillAndStroke")}/>
				</RowLR>

				{/* ⚠ PROMINENT warning: Fill/Fill+stroke on an open path is dropped (no interior) —
					so it places nothing AND suppresses nothing. Condition mirrors the bridge guard. */}
				{(sf.mode == "fill" || sf.mode == "fillAndStroke") && !path.curve_closed && <Row mt={2} p={6} style={{backgroundColor: "rgba(220,50,50,.16)", border: "1px solid rgba(220,50,50,.65)", borderRadius: 4}}>
					<Text style={{color: "#ff6b6b", fontWeight: "bold", fontSize: 13}}>
						⚠ Inactive region: this path is not marked Closed, and Fill regions need a closed interior — the placement system DROPS this region, so it places nothing AND suppresses nothing. Set the curve's "Closed" checkbox above, or switch Mode to "Stroke" (works on open paths — recommended for roads).
					</Text>
				</Row>}

				{/* Stroke width — the FULL stroke thickness. */}
				{(sf.mode == "stroke" || sf.mode == "fillAndStroke") && <RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Full width in metres (the total stroke thickness — extends half this on EACH side of
						the polyline).  Ignored for plain Fill.  A closed path with fillAndStroke spills
						this far OUTSIDE the ring too (the apron).
					`.AsMultiline(0)}>Stroke width (m):</TextPlus>
					<Spinner step="any" min={0} value={sf.strokeWidth}
						onChange={val=>ChangeFeature(f=>f.strokeWidth = val != null && val > 0 ? val : undefined)}/>
					<Button ml={5} mdIcon="delete" title="Clear (use default)" onClick={()=>ChangeFeature(f=>f.strokeWidth = undefined)}/>
				</RowLR>}

				{/* Feather — the density falloff band at the region boundary (a soft forest edge). */}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Metres over which density ramps to zero at the region boundary (a soft forest edge,
						Set13 §3.1).  The kind's densityAt is multiplied by a smoothstep ramp over this band.
						0 = a razor-sharp edge.  Larger = a more gradual fade-out into surrounding empty ground.
					`.AsMultiline(0)}>Feather (m):</TextPlus>
					<Spinner step="any" min={0} value={sf.feather}
						onChange={val=>ChangeFeature(f=>f.feather = val != null && val >= 0 ? val : 0)}/>
					<Button ml={5} mdIcon="delete" title="Reset to default (8)" onClick={()=>ChangeFeature(f=>f.feather = 8)}/>
				</RowLR>

				{/* Density overrides — per-region overrides of the kind's spacing/presence. */}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Per-region override of the kind's MEAN SPACING (metres) between placement candidates.
						Smaller = denser.  Absent = the kind's own spacing default (a PathSceneryFeature may
						override it, Set13 §3.1).  Placement is per-CELL (§3.3), so this is invariant to tile size.
						The "Placed" count shows how many instances this feature places at full density for the
						current spacing — the live readout for dialling in density.
					`.AsMultiline(0)}>Spacing (m):</TextPlus>
					<Spinner step="any" min={0} value={sf.spacingOverride}
						onChange={val=>ChangeFeature(f=>f.spacingOverride = val != null && val > 0 ? val : undefined)}/>
					<Button ml={5} mdIcon="delete" title="Clear (use the kind's default)" onClick={()=>ChangeFeature(f=>f.spacingOverride = undefined)}/>
					{placedCount != null && <Text ml={10} style={{alignSelf: "center", opacity: 0.8}}>Placed: {placedCount}</Text>}
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Per-region override of the kind's PRESENCE (0..1) — the fraction of candidate cells
						that actually spawn (the Set3 §4.1 presence gate).  This is the region's primary density
						knob: 1 = every cell, 0 = none.  Absent = the kind's presence default.
					`.AsMultiline(0)}>Presence:</TextPlus>
					<Spinner step="any" min={0} max={1} value={sf.presenceOverride}
						onChange={val=>ChangeFeature(f=>f.presenceOverride = val != null && val >= 0 && val <= 1 ? val : undefined)}/>
					<Button ml={5} mdIcon="delete" title="Clear (use the kind's default)" onClick={()=>ChangeFeature(f=>f.presenceOverride = undefined)}/>
				</RowLR>

				{/* Dominance — precedence for suppression (matches PathMaterialFeature.dominance). */}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Explicit dominance (higher wins) — overrides the auto-derived value.  Absent (default):
						derived from containment nesting among SCENERY regions: the number of other
						interior-bearing scenery regions strictly containing this one (a ring inside two rings
						= 2); strokes sit above all fills by default.  Dominance matters only for
						"Suppress prior scenery" (below): a region only suppresses regions of STRICTLY LOWER
						dominance.  Otherwise scenery regions coexist (a forest and a river-stroke can
						overlap and both place).
					`.AsMultiline(0)}>Dominance:</TextPlus>
					<Spinner step="any" value={sf.dominance}
						onChange={val=>ChangeFeature(f=>f.dominance = val != null ? val : undefined)}/>
					<Button ml={5} mdIcon="delete" title="Clear (use derived nesting depth)" onClick={()=>ChangeFeature(f=>f.dominance = undefined)}/>
				</RowLR>

				{/* Suppression — cut out scenery placed by lower-dominance regions over this area. */}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						The fraction (0..1) of scenery instances that would otherwise be placed over this
						region's area by scenery regions of STRICTLY LOWER dominance, which are removed.  Null/0
						= no effect; 1 = fully clear.  Works with no kinds set (a suppression-only feature:
						this path places nothing itself, only thins) — set Kinds to none and use this to carve
						a clearing out of an enclosing forest.  The cut is feathered at this region's boundary
						(the Feather field above softens it), and applied per placement cell, so ~X% of the
						underlying instances are removed in aggregate.  Regions of equal or higher dominance
						are unaffected.
					`.AsMultiline(0)}>Suppress prior scenery:</TextPlus>
					<Spinner step="any" min={0} max={1} value={sf.suppressPriorScenery}
						onChange={val=>ChangeFeature(f=>f.suppressPriorScenery = val != null && val >= 0 && val <= 1 ? val : undefined)}/>
					<Button ml={5} mdIcon="delete" title="Clear (no suppression)" onClick={()=>ChangeFeature(f=>f.suppressPriorScenery = undefined)}/>
				</RowLR>

				{/* Masks — narrow where this region places objects. */}
				<SceneryMasks_UI value={sf.masks ?? []} onChange={v=>ChangeFeature(f=>f.masks = v)}/>
			</>}
			<Row mt={8}>
				<Button text="Export area for unit-tests" onClick={()=>{
					ShowSceneryExportDialog(obj, path, allMapObjects, map.id);
				}}/>
			</Row>
		</Column>
	);
});

/** `elevationBand` / `slopeBand` filters narrowing where this region places objects (Set12 §3.5,
 *  reused verbatim for scenery via `MaterialMask` — "no trees above the treeline", "no trees on
 *  cliffs", Set13 §3.4).  SAME class as materials, so the vocabulary and its kind-aware feather
 *  defaults are shared — not reimplemented.  Evaluated per sample on the placement path, so a mask
 *  edit re-derives density but never recompiles a shader. */
const SceneryMasks_UI = observer_mgl((props: {
	value: MaterialMask[];
	onChange: (v: MaterialMask[])=>void;
})=>{
	const {value, onChange} = props;
	const ChangeMask = (i: number, mutator: (m: MaterialMask)=>any)=>{
		const next = [...value];
		const mask = Clone(next[i]);
		mutator(mask);
		next[i] = mask;
		onChange(next);
	};
	return (
		<>
			<Row mt={5} style={{fontWeight: "bold"}}>Masks:</Row>
			{value.map((mask, i)=>(
				<Column key={i} mt={3} style={{background: "rgba(255,255,255,.08)", padding: 3, borderRadius: 4}}>
					<Row>
						<Select displayType="button bar" options={[
							{name: "Elevation band", value: "elevationBand"},
							{name: "Slope band", value: "slopeBand"},
						]} value={mask.kind} onChange={val=>ChangeMask(i, m=>({...(m as any), kind: val}))}/>
						<Button ml="auto" mdIcon="delete" title="Remove this mask" onClick={()=>onChange(value.filter((_, j)=>j != i))}/>
					</Row>
					<Row mt={3}>
						<Text>Min:</Text>
						<Spinner ml={3} step="any"
							value={mask.min} onChange={val=>ChangeMask(i, m=>m.min = val)}/>
						<Text ml={5}>Max:</Text>
						<Spinner ml={3} step="any"
							value={mask.max} onChange={val=>ChangeMask(i, m=>m.max = val)}/>
						<Text ml={5}>Feather:</Text>
						<Spinner ml={3} step="any" min={0} placeholder={mask.kind == "slopeBand" ? "0.1" : "2"}
							value={mask.feather} onChange={val=>ChangeMask(i, m=>m.feather = val)}/>
						<Button ml={5} mdIcon="delete" title="Clear feather (use the kind-aware default)" onClick={()=>ChangeMask(i, m=>m.feather = undefined)}/>
					</Row>
					<Row mt={2} style={{opacity: 0.6, fontSize: 11}}>
						<Text>{mask.kind == "elevationBand"
							? `Only inside elevation ${mask.min}..${mask.max} m (feather default 2 m).`
							: `Only inside slope ${mask.min}..${mask.max} m/m (feather default 0.1 m/m ~6°).`}</Text>
					</Row>
				</Column>
			))}
			<Row mt={3}>
				<Button ml={0} p="1px 8px" text="+ elevation band" title="Narrow this region to an elevation range (e.g. no trees above the treeline)"
					onClick={()=>onChange([...value, {kind: "elevationBand", min: 0, max: 100}])}/>
				<Button ml={5} p="1px 8px" text="+ slope band" title="Narrow this region to a slope range (e.g. no trees on cliffs)"
					onClick={()=>onChange([...value, {kind: "slopeBand", min: 0.5, max: 2}])}/>
			</Row>
			<Row mt={2} style={{opacity: 0.6, fontSize: 11}}>
				<Text>Masks multiply the region's density: a sample outside every mask is treated as outside the region.</Text>
			</Row>
		</>
	);
});
